import type { Station } from '@/types/stationTypes'
import { findStation, getSearchStationCode } from '@/utils/stationAgglomeration'

export interface StationSuggestion {
  // The row's own station code, unique within the list.
  key: string
  // The code a pick searches by: the agglomeration's for every row in its group.
  code: string
  name: string
  district?: string
  county?: string
  isChild: boolean
}

const matchesQuery = (station: Station, query: string) =>
  station.name.toLowerCase().includes(query) ||
  (station.synonyms ?? []).some((synonym) => synonym.toLowerCase().includes(query))

const collator = new Intl.Collator('nb-NO', { sensitivity: 'base' })

const compareNames = (a: Station, b: Station) => collator.compare(a.name, b.name)

const compareBySequence = (a: Station, b: Station) =>
  (a.sequence_number ?? 999) - (b.sequence_number ?? 999) || compareNames(a, b)

// Names starting with the query come first, ordered by sequence number; the rest alphabetically.
const compareByQuery = (a: Station, b: Station, query: string) => {
  const startsWithA = a.name.toLowerCase().startsWith(query)
  const startsWithB = b.name.toLowerCase().startsWith(query)

  if (startsWithA && !startsWithB) return -1
  if (!startsWithA && startsWithB) return 1
  if (startsWithA && startsWithB) return compareBySequence(a, b)
  return compareNames(a, b)
}

const toSuggestion = (station: Station, head: Station): StationSuggestion => ({
  key: station._u_i_c_station_code,
  code: head._u_i_c_station_code,
  name: station.name,
  district: station.district,
  county: station.county,
  isChild: station !== head,
})

/**
 * Autocomplete rows for the stations matching the query (by name or synonym).
 *
 * An agglomeration is listed as its own row followed by its member stops, and the whole group is
 * shown when any row in it matches. Every row in a group selects the agglomeration.
 *
 * @param candidates Stations that may be suggested
 * @param searchQuery The text typed by the user
 * @param options.allStations Full station list, used to find an agglomeration missing from `candidates`
 * @param options.excludeCode Search code of a group to leave out, such as the origin's
 */
export const getSortedStationSuggestions = (
  candidates: Station[],
  searchQuery: string,
  { allStations = candidates, excludeCode }: { allStations?: Station[]; excludeCode?: string } = {},
): StationSuggestion[] => {
  const query = searchQuery.toLowerCase().trim()
  if (!query) return []

  const groups = new Map<string, { head?: Station; members: Station[] }>()
  for (const station of candidates) {
    const code = getSearchStationCode(station, allStations)
    if (code === excludeCode) continue
    const group = groups.get(code) ?? { members: [] }
    if (code === station._u_i_c_station_code) {
      group.head = station
    } else {
      group.members.push(station)
    }
    groups.set(code, group)
  }

  const matchingGroups = [...groups.entries()].flatMap(([code, group]) => {
    const head = group.head ?? findStation(code, allStations)
    if (!head) return []

    let best: Station | undefined
    for (const station of [head, ...group.members]) {
      if (matchesQuery(station, query) && (!best || compareByQuery(station, best, query) < 0)) {
        best = station
      }
    }
    return best ? [{ head, members: group.members, best }] : []
  })

  return matchingGroups
    .sort((a, b) => compareByQuery(a.best, b.best, query))
    .flatMap(({ head, members }) => [
      toSuggestion(head, head),
      ...members.sort(compareBySequence).map((member) => toSuggestion(member, head)),
    ])
}
